import React, { useMemo } from 'react';
import { TiktokAudioPostStats } from '@round/api';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { Column } from 'react-table';
import { buildTiktokPostUrl, numberWithCommas } from '../../../../../../../../helpers';
import styles from './TopPostsTable.module.css';
import Skeleton from '../../../../../../../../ui/Skeleton/Skeleton';
import { OrderByParam } from '../../../../../../../../Hooks/useReactTableSortToOrderBy';
import TruncatedTextCell from '../../../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import cn from 'classnames';
import CountryCell from '../../../../../../../../SharedComponents/TableComponents/CountryCell/CountryCell';

type TopPostsTableProps = {
    rows: TiktokTopPostsRow[];
    page: number;
    pageSize: number;
    onPageSizeChange: (pageSize: number | undefined) => void;
    onPageChange: (page: number) => void;
    count: number;
    loading: boolean;
    orderBy: OrderByParam<TiktokTopPostsRow>;
    onOrderByChange: (orderBy: OrderByParam<TiktokTopPostsRow>) => void;
    noDataLabel?: string;
} & Pick<PaginationTableProps<TiktokTopPostsRow>, 'onPaginationChange'>;

export type TiktokTopPostsRow = Omit<TiktokAudioPostStats, 'author_follower_count'> & {
    author_follower_count: number | null;
    sponsored?: boolean;
    user_id?: number | null;
};

const TopPostsTable = ({
    page,
    onPageChange,
    onPageSizeChange,
    noDataLabel,
    pageSize,
    rows,
    count,
    loading,
    orderBy,
    onOrderByChange,
    onPaginationChange,
}: TopPostsTableProps) => {
    const showSponsored = useMemo(() => rows.some((row) => row.sponsored), [rows]);
    const columns = useMemo(() => {
        const columns: Column<TiktokTopPostsRow>[] = [
            {
                Header: 'Username',
                accessor: 'author_unique_id',
                disableSortBy: true,
                Cell: ({ value }) => (loading ? <Skeleton /> : <TruncatedTextCell>{value}</TruncatedTextCell>),
            },
            {
                Header: 'Followers',
                accessor: 'author_follower_count',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{value === null ? '-' : numberWithCommas(value)}</>;
                },
            },
            {
                Header: 'Location',
                accessor: 'location',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <CountryCell countryCode={value} />;
                },
            },
            {
                Header: 'Description',
                accessor: 'description',
                disableSortBy: true,
                Cell: ({ value, row: { original } }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return (
                        <a
                            className={styles.description}
                            href={buildTiktokPostUrl(original.author_unique_id, original.video_tiktok_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <TruncatedTextCell>{value || 'No Caption'}</TruncatedTextCell>
                        </a>
                    );
                },
            },
            {
                Header: 'Date posted',
                accessor: 'create_time',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : new Date(value * 1000).toLocaleDateString('en-GB')}</>,
            },
            {
                Header: 'Boosted',
                accessor: 'is_ad',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{value ? 'Yes' : 'No'}</>;
                },
            },
            {
                Header: 'Views',
                accessor: 'play_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : numberWithCommas(value)}</>,
            },
            {
                Header: 'Likes',
                accessor: 'like_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : numberWithCommas(value)}</>,
            },
            {
                Header: 'Comments',
                accessor: 'comment_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : numberWithCommas(value)}</>,
            },
            {
                Header: 'Shares',
                accessor: 'share_count',
                Cell: ({ value }) => <>{loading ? <Skeleton /> : numberWithCommas(value)}</>,
            },
            {
                Header: 'Saves',
                accessor: 'save_count',
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return <>{value ? numberWithCommas(value) : '-'}</>;
                },
            },
        ];

        if (showSponsored) {
            columns.splice(5, 0, {
                Header: 'Sponsored',
                accessor: 'sponsored',
                disableSortBy: true,
                Cell: ({ value }) => {
                    if (loading) {
                        return <Skeleton />;
                    }

                    return value ? 'Yes' : 'No';
                },
            } as Column<TiktokTopPostsRow>);
        }

        return columns;
    }, [loading, showSponsored]);

    return (
        <PaginationTable
            disableSortBy={false}
            manualSortBy
            manualPagination
            data={rows}
            columns={columns}
            page={page}
            pageSize={pageSize}
            setPageSize={onPageSizeChange}
            setPage={onPageChange}
            loading={loading}
            count={count}
            noDataLabel={noDataLabel}
            orderBy={orderBy}
            onOrderByChange={onOrderByChange}
            tableClassName={styles.table}
            theadClassName={cn(styles.thead, { [styles.showSponsored]: showSponsored })}
            tbodyClassName={styles.tbody}
            onPaginationChange={onPaginationChange}
        />
    );
};

export default TopPostsTable;
